import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { fmtMonthLabel } from "./format";

const fmtUSD = (v, lang = "en") => {
  const num = Math.round(v).toLocaleString(lang === "it" ? "it-IT" : "en-US", {
    useGrouping: "always",
  });
  return lang === "it" ? `${num} $` : "$" + num;
};

export function exportSimulationPDF({ sim, meta, lang }) {
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
  const pageW = doc.internal.pageSize.getWidth();

  // Header: dark bar
  doc.setFillColor(11, 13, 18);
  doc.rect(0, 0, pageW, 80, "F");

  // Gold accent
  doc.setFillColor(212, 164, 74);
  doc.rect(0, 78, pageW, 2, "F");

  // Brand text
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(245, 199, 110);
  doc.text("WordCripto", 40, 42);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(164, 155, 138);
  doc.text(
    lang === "it"
      ? "Simulatore d'investimento a 10 anni"
      : "10-year investment simulator",
    40,
    58
  );

  // Right side: date
  doc.setFontSize(9);
  doc.setTextColor(200, 200, 200);
  const today = new Date().toLocaleDateString(lang === "it" ? "it-IT" : "en-US");
  doc.text(`${lang === "it" ? "Emesso il" : "Issued"}: ${today}`, pageW - 40, 42, { align: "right" });
  doc.text(
    `${lang === "it" ? "Inizio" : "Start"}: ${meta.startLabel}`,
    pageW - 40,
    58,
    { align: "right" }
  );

  // KPI section
  let y = 110;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(30, 30, 30);
  doc.text(lang === "it" ? "Riepilogo simulazione" : "Simulation summary", 40, y);
  y += 16;

  const kpis = [
    [lang === "it" ? "Totale versato" : "Total deposited", fmtUSD(sim.total_deposited, lang)],
    [lang === "it" ? "Totale reinvestito" : "Total reinvested", fmtUSD(sim.total_reinvested, lang)],
    [lang === "it" ? "Totale ritirato" : "Total withdrawn", fmtUSD(sim.total_withdrawn, lang)],
    [lang === "it" ? "Capitale finale" : "Final capital", fmtUSD(sim.final_capital, lang)],
    [lang === "it" ? "Liquidità finale" : "Final cash", fmtUSD(sim.final_liquidity, lang)],
    [lang === "it" ? "Totale finale" : "Final total", fmtUSD(sim.final_total, lang)],
    [lang === "it" ? "Crescita netta" : "Net growth", fmtUSD(sim.total_growth, lang)],
  ];

  autoTable(doc, {
    startY: y,
    body: kpis,
    theme: "plain",
    styles: { fontSize: 10, cellPadding: 6, textColor: [40, 40, 40] },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 180 },
      1: { halign: "right", textColor: [143, 106, 31], fontStyle: "bold" },
    },
    margin: { left: 40, right: 40 },
  });

  // Monthly table
  const head = [[
    "#",
    lang === "it" ? "Mese" : "Month",
    lang === "it" ? "Versa" : "Dep",
    lang === "it" ? "Cap. inizio" : "Cap. start",
    lang === "it" ? "Liq. inizio" : "Cash start",
    lang === "it" ? "Guadagno" : "Profit",
    lang === "it" ? "Ritiro" : "Withdraw",
    lang === "it" ? "Reinv." : "Reinv.",
    lang === "it" ? "Liq. fine" : "Cash end",
    lang === "it" ? "Cap. fine" : "Cap. end",
  ]];

  const body = sim.rows.map((r, i) => [
    i + 1,
    fmtMonthLabel(r, lang),
    r.deposit ? fmtUSD(r.deposit, lang) : "—",
    fmtUSD(r.capital_start, lang),
    fmtUSD(r.liquidity_start, lang),
    fmtUSD(r.month_profit, lang),
    r.withdrawal ? fmtUSD(r.withdrawal, lang) : "—",
    r.reinvested ? fmtUSD(r.reinvested, lang) : "—",
    fmtUSD(r.liquidity_end, lang),
    fmtUSD(r.capital_end, lang),
  ]);

  autoTable(doc, {
    head,
    body,
    startY: doc.lastAutoTable.finalY + 20,
    styles: { fontSize: 7.5, cellPadding: 3 },
    headStyles: { fillColor: [20, 24, 33], textColor: [245, 199, 110], fontStyle: "bold" },
    alternateRowStyles: { fillColor: [248, 246, 240] },
    columnStyles: {
      0: { halign: "right", cellWidth: 22, textColor: [130, 130, 130] },
      1: { fontStyle: "bold" },
      2: { halign: "right" },
      3: { halign: "right" },
      4: { halign: "right", textColor: [130, 130, 130] },
      5: { halign: "right", textColor: [50, 130, 60] },
      6: { halign: "right" },
      7: { halign: "right", textColor: [180, 100, 40] },
      8: { halign: "right", textColor: [130, 130, 130] },
      9: { halign: "right", fontStyle: "bold", textColor: [143, 106, 31] },
    },
    didParseCell: (data) => {
      if (data.section === "body" && sim.rows[data.row.index]?.is_year_start) {
        data.cell.styles.fillColor = [235, 220, 190];
        data.cell.styles.fontStyle = "bold";
      }
    },
    margin: { left: 40, right: 40 },
  });

  // Footer on last page
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(
      "Wordcripto Unipessoal LDA · Company #519515471 · wordcripto.com",
      40,
      doc.internal.pageSize.getHeight() - 24
    );
    doc.text(
      `${p} / ${pageCount}`,
      pageW - 40,
      doc.internal.pageSize.getHeight() - 24,
      { align: "right" }
    );
  }

  doc.save(`WordCripto-Simulazione-${meta.startLabel.replace(" ", "-")}.pdf`);
}
